import { useState } from "react";

interface Props {
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    desc: string;
    category: string;
    deadline: string;
    roles: { name: string; desc: string; icon: string }[];
  }) => void;
}

const CATEGORIES = ["이미지 생성", "콘텐츠", "비즈니스", "영상 제작", "개발", "마케팅"];
const ROLE_ICONS = ["🎨", "✍️", "🎵", "🎬", "🔍", "📊", "💡", "🖼️", "📝", "🤖"];

const DEFAULT_ROLES = [
  { name: "", desc: "", icon: "🎨" },
  { name: "", desc: "", icon: "✍️" },
];

export default function CreatePartyModal({ onClose, onSubmit }: Props) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [category, setCategory] = useState("");
  const [deadline, setDeadline] = useState("D-3");
  const [roles, setRoles] = useState(DEFAULT_ROLES.map((r) => ({ ...r })));

  const addRole = () => {
    if (roles.length >= 4) return;
    setRoles((p) => [...p, { name: "", desc: "", icon: ROLE_ICONS[p.length % ROLE_ICONS.length] }]);
  };

  const removeRole = (i: number) => {
    if (roles.length <= 2) return;
    setRoles((p) => p.filter((_, idx) => idx !== i));
  };

  const updateRole = (i: number, field: "name" | "desc" | "icon", value: string) => {
    setRoles((p) => p.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };

  const canNext1 = title.trim() && category;
  const canSubmit = roles.every((r) => r.name.trim());

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ title, desc, category, deadline, roles });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-7 pt-7 pb-5 border-b border-[#F2F4F6] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {[1, 2].map((s) => (
                <div
                  key={s}
                  className={`w-2 h-2 rounded-full transition-all ${step >= s ? "bg-[#00D873]" : "bg-[#E4E6EA]"}`}
                />
              ))}
            </div>
            <h3 className="text-lg font-black text-[#191F28]">
              {step === 1 ? "파티 기본 설정" : "역할 배분 설정"}
            </h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-[#8B95A1] hover:text-[#191F28] cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-7 space-y-5 max-h-[60vh] overflow-y-auto">
          {step === 1 ? (
            <>
              <div>
                <label className="text-xs font-bold text-[#191F28] block mb-2">챌린지 제목 *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="예: AI로 브랜드 로고 만들기"
                  className="w-full bg-[#F2F4F6] rounded-xl px-4 py-3 text-sm text-[#191F28] placeholder-[#8B95A1] outline-none focus:ring-2 focus:ring-[#00D873]/30"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-[#191F28] block mb-2">설명 (선택)</label>
                <textarea
                  value={desc}
                  onChange={(e) => setDesc(e.target.value.slice(0, 200))}
                  placeholder="어떤 걸 함께 만들 건가요? 간단히 설명해주세요."
                  className="w-full bg-[#F2F4F6] rounded-xl px-4 py-3 text-sm text-[#191F28] placeholder-[#8B95A1] outline-none focus:ring-2 focus:ring-[#00D873]/30 resize-none h-20"
                />
                <div className="text-xs text-[#8B95A1] text-right mt-1">{desc.length}/200</div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#191F28] block mb-2">카테고리 *</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setCategory(cat)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                        category === cat ? "bg-[#191F28] text-white" : "bg-[#F2F4F6] text-[#8B95A1] hover:text-[#191F28]"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#191F28] block mb-2">마감 기한</label>
                <div className="flex gap-2">
                  {["D-1", "D-3", "D-5", "D-7"].map((d) => (
                    <button
                      key={d}
                      onClick={() => setDeadline(d)}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
                        deadline === d ? "bg-[#00D873] text-white" : "bg-[#F2F4F6] text-[#8B95A1]"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-[#8B95A1]">각 팀원이 맡을 역할을 설정해주세요. (2~4개)</p>
              {roles.map((role, i) => (
                <div key={i} className="bg-[#F2F4F6] rounded-2xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{role.icon}</span>
                      <span className="text-xs font-bold text-[#191F28]">역할 {i + 1}</span>
                    </div>
                    {roles.length > 2 && (
                      <button
                        onClick={() => removeRole(i)}
                        className="text-[#C8CDD4] hover:text-[#FF6B6B] cursor-pointer transition-colors"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {ROLE_ICONS.map((ic) => (
                      <button
                        key={ic}
                        onClick={() => updateRole(i, "icon", ic)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm cursor-pointer transition-all ${
                          role.icon === ic ? "bg-[#191F28]" : "bg-white hover:bg-[#E8E9EB]"
                        }`}
                      >
                        {ic}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={role.name}
                    onChange={(e) => updateRole(i, "name", e.target.value)}
                    placeholder="역할 이름 (예: 이미지 담당)"
                    className="w-full bg-white rounded-xl px-3 py-2.5 text-sm text-[#191F28] placeholder-[#8B95A1] outline-none focus:ring-2 focus:ring-[#00D873]/30"
                  />
                  <input
                    type="text"
                    value={role.desc}
                    onChange={(e) => updateRole(i, "desc", e.target.value)}
                    placeholder="설명 (예: Midjourney로 이미지 생성)"
                    className="w-full bg-white rounded-xl px-3 py-2.5 text-sm text-[#191F28] placeholder-[#8B95A1] outline-none focus:ring-2 focus:ring-[#00D873]/30"
                  />
                </div>
              ))}
              {roles.length < 4 && (
                <button
                  onClick={addRole}
                  className="w-full py-3 rounded-2xl border-2 border-dashed border-[#E4E6EA] text-sm text-[#8B95A1] hover:border-[#00D873] hover:text-[#00D873] cursor-pointer transition-all flex items-center justify-center gap-2"
                >
                  <i className="ri-add-line" />
                  역할 추가 (최대 4개)
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 pb-7 flex gap-3">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="flex-1 py-3.5 rounded-2xl border-2 border-[#E4E6EA] text-sm font-bold text-[#191F28] cursor-pointer hover:bg-[#F2F4F6] transition-colors whitespace-nowrap"
            >
              이전
            </button>
          )}
          {step === 1 ? (
            <button
              onClick={() => canNext1 && setStep(2)}
              disabled={!canNext1}
              className={`flex-1 py-3.5 rounded-2xl text-sm font-black text-white transition-all whitespace-nowrap ${
                canNext1 ? "bg-[#00D873] cursor-pointer hover:opacity-90" : "bg-[#E4E6EA] cursor-not-allowed"
              }`}
            >
              다음 단계 →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`flex-1 py-3.5 rounded-2xl text-sm font-black text-white transition-all whitespace-nowrap ${
                canSubmit ? "bg-[#191F28] cursor-pointer hover:bg-[#00D873]" : "bg-[#E4E6EA] cursor-not-allowed"
              }`}
            >
              🎮 파티 만들기!
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
